import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from './auth';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('resetToken') || '';

  const mode = useMemo(() => (resetToken ? 'reset' : 'login'), [resetToken]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState('');

  const parseResponseBody = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return { message: text };
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
    setDevResetUrl('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await parseResponseBody(response);
      if (!response.ok) {
        setError(data.message || 'Login failed');
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err.message || 'Unable to login right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });

      const data = await parseResponseBody(response);
      if (!response.ok) {
        setError(data.message || 'Unable to process request');
        return;
      }

      setSuccess(data.message || 'If that email exists, a reset link was sent.');
      if (data.devResetUrl) {
        setDevResetUrl(data.devResetUrl);
      }
    } catch (err) {
      setError(err.message || 'Unable to process request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password: newPassword }),
      });

      const data = await parseResponseBody(response);
      if (!response.ok) {
        setError(data.message || 'Reset failed');
        return;
      }

      setSuccess('Password updated successfully. You can log in now.');
      setTimeout(() => navigate('/login'), 900);
    } catch (err) {
      setError(err.message || 'Unable to reset password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 96px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 3,
        background:
          'radial-gradient(circle at 14% 18%, rgba(17, 47, 71, 0.16), transparent 38%), radial-gradient(circle at 86% 4%, rgba(206, 200, 191, 0.5), transparent 34%), #f4f6f8',
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 960,
          border: '1px solid var(--ui-border)',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            background: 'linear-gradient(165deg, #0d2538 0%, #112f47 62%, #435a6d 100%)',
            color: '#ffffff',
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 0.75 }}>
            <Box
              component="img"
              src="/TRS%20Logo-No%20BG.png"
              alt="TRS logo"
              sx={{ width: 180, maxWidth: '100%', height: 'auto' }}
            />
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#ece8e2', fontWeight: 700, letterSpacing: 0.35 }}>
            Tennessee Rail Systems
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 1.4 }}>
            Transload Portal
          </Typography>
          <Typography sx={{ color: '#ece8e2', maxWidth: 360, mb: 3 }}>
            Manage railcars, truck orders, and BOL workflows in one place.
          </Typography>
        </Box>

        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {mode === 'reset' ? (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--ui-brand)', mb: 0.75 }}>
                Reset Password
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--ui-muted)', mb: 3 }}>
                Enter a new password to finish resetting your account.
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              <Box component="form" onSubmit={handleResetSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    fullWidth
                  />
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save New Password'}
                  </Button>
                  <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
                    Back to login
                  </Link>
                </Stack>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--ui-brand)', mb: 0.75 }}>
                {showForgot ? 'Forgot Password' : 'Sign In'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--ui-muted)', mb: 3 }}>
                {showForgot
                  ? 'Enter your email and we will send you a reset link.'
                  : 'Use your account credentials to access the portal.'}
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              {devResetUrl && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Dev reset link:{' '}
                  <Link href={devResetUrl} target="_blank" rel="noreferrer">
                    open reset page
                  </Link>
                </Alert>
              )}

              {!showForgot ? (
                <Box component="form" onSubmit={handleLoginSubmit}>
                  <Stack spacing={2}>
                    <TextField
                      type="email"
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      fullWidth
                    />
                    <TextField
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      fullWidth
                    />
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                      {isSubmitting ? 'Signing In...' : 'Sign In'}
                    </Button>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                      onClick={() => {
                        clearMessages();
                        setShowForgot(true);
                        setForgotEmail(email || '');
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Stack>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleForgotSubmit}>
                  <Stack spacing={2}>
                    <TextField
                      type="email"
                      label="Email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      fullWidth
                    />
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                    <Divider />
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                      onClick={() => {
                        clearMessages();
                        setShowForgot(false);
                      }}
                    >
                      Back to sign in
                    </Link>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;

